// ============================================================
//  PAST-DUE SUBSCRIPTIONS PAGE — ADMIN DASHBOARD
//  Path: src/pages/PastDueSubscriptionsPage.tsx
//
//  Experts whose subscription charge has been failing long
//  enough (EXPERT_SUBSCRIPTION_ADMIN_REVIEW_AFTER_DAYS — default
//  30 — see env.js) to warrant a human decision, rather than
//  another automated retry or reminder. By this point:
//    - The billing cron has already auto-retried the charge
//      every EXPERT_SUBSCRIPTION_RETRY_INTERVAL_DAYS
//    - The expert has already been restricted from browsing/
//      applying to NEW tasks since day
//      EXPERT_SUBSCRIPTION_RESTRICT_AFTER_DAYS (default 14) —
//      see requireApprovedExpert.middleware.js
//    - Jobs they were already hired for were NEVER affected —
//      this page is purely about ongoing platform access
//
//  No approve/suspend action lives directly on this page — the
//  actual decision (suspend, or manually mark resolved once
//  they've sorted out payment outside the app) uses the same
//  status controls already on ExpertsPage/ExpertDetailPage, so
//  this stays a focused review queue rather than duplicating
//  that logic.
//
//  Access:
//    super_admin, admin  → full access
//    customer_service    → view only (matches every other page
//                           in this section)
// ============================================================

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listPastDueSubscriptionExperts } from '../services/admin.service'
import { useAuth } from '../context/AuthContext'

interface PastDueExpert {
  id: string
  pastDueSince: string | null
  subscriptionRenewsAt: string | null
  user: { id: string; firstName: string; lastName: string; email: string | null; phone: string | null }
}

const daysSince = (dateStr: string | null) => {
  if (!dateStr) return null
  const ms = Date.now() - new Date(dateStr).getTime()
  return Math.floor(ms / (1000 * 60 * 60 * 24))
}

const PastDueSubscriptionsPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const canModify = user?.roles.includes('super_admin') || user?.roles.includes('admin')

  const [experts, setExperts] = useState<PastDueExpert[]>([])
  const [loading, setLoading] = useState(true)

  const fetchExperts = async () => {
    setLoading(true)
    try {
      const res = await listPastDueSubscriptionExperts()
      setExperts(res.data.data)
    } catch (err) {
      console.error('Failed to fetch past-due subscription experts:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchExperts() }, [])

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Past-Due Subscriptions</h1>
          <p className="text-gray-400 text-sm mt-1">{experts.length} expert{experts.length === 1 ? '' : 's'} unpaid 30+ days — needs a manual decision</p>
        </div>
        {!canModify && (
          <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
        )}
      </div>

      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : experts.length === 0 ? (
          <div className="text-center py-8 text-gray-400">Nobody's currently past this threshold — the billing cron and grace period are handling everything automatically.</div>
        ) : (
          experts.map((expert) => {
            const days = daysSince(expert.pastDueSince)
            return (
              <div key={expert.id} onClick={() => navigate(`/experts/${expert.id}`)}
                className="bg-surface-card border border-border-light rounded-2xl p-5 cursor-pointer hover:bg-surface-tertiary/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-700 flex items-center justify-center text-white font-bold">
                      {expert.user.firstName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">{expert.user.firstName} {expert.user.lastName}</p>
                      <p className="text-sm text-gray-400">{expert.user.email || expert.user.phone || '—'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Past due since: {expert.pastDueSince ? new Date(expert.pastDueSince).toLocaleDateString() : '—'}
                      </p>
                    </div>
                  </div>
                  <span className="px-3 py-1 text-xs rounded-full bg-red-600/20 text-red-400">
                    {days != null ? `${days} days unpaid` : 'Unpaid'}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-sm">
                  <span className="px-2 py-1 rounded-full text-xs bg-yellow-600/20 text-yellow-400">
                    🔒 Restricted from new tasks
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs bg-surface-tertiary text-gray-300">
                    ✓ Jobs already hired for are unaffected
                  </span>
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  Click to open their profile and decide next steps (suspend, or mark resolved once payment is sorted out).
                </p>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default PastDueSubscriptionsPage